import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Check, Heart } from "lucide-react";
import { Shell, Btn, Field, dateRange } from "@/components/album/ui";
import Lightbox from "@/components/album/Lightbox";
import { extOf, fetchOriginal, likePhoto, manage } from "@/lib/api";
import { TOP_FAVORITES, topFavorites } from "@/lib/favorites";
import { ZipBuilder } from "@/lib/zip";
import { cn } from "@/lib/utils";

const GOLD = "#C6A56B";
const PART_LIMIT = 400 * 1024 * 1024; // satu ZIP maksimal ~400 MB, lebih dari itu dipecah
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const slug = (s) => String(s || "album").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "album";
const safe = (s) => String(s || "tamu").replace(/[\\/:*?"<>|\u0000-\u001f]/g, "").trim().slice(0, 30) || "tamu";
const save = (blob, name) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 60000);
};

export default function CoupleAlbum() {
  const { id } = useParams();
  const key = `dirga:pin:${id}`;
  const [pin, setPin] = useState(sessionStorage.getItem(key) || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [fatal, setFatal] = useState(null);
  const [checking, setChecking] = useState(false);
  const [sel, setSel] = useState([]);
  const [tab, setTab] = useState("all");
  const [view, setView] = useState(null); // { pids, index, dir }
  const [zip, setZip] = useState(null); // { done, total } selama mengunduh

  const load = useCallback(async (p) => {
    const d = await manage(id, p);
    setData(d);
    return d;
  }, [id]);

  useEffect(() => {
    if (!pin) return;
    load(pin).catch((e) => {
      if (e.status === 401) {
        sessionStorage.removeItem(key);
        setPin("");
      } else setFatal(e);
    });
  }, [pin, load, key]);

  const login = async () => {
    setChecking(true);
    try {
      await load(input);
      sessionStorage.setItem(key, input);
      setPin(input);
    } catch (e) {
      if (e.status === 404) setFatal(e);
      else toast.error(e.message);
    }
    setChecking(false);
  };

  const patch = useCallback(
    (pid, change) => setData((d) => (d ? { ...d, photos: d.photos.map((x) => (x.pid === pid ? { ...x, ...change } : x)) } : d)),
    []
  );
  const like = useCallback(
    async (p) => {
      const on = !p.liked;
      patch(p.pid, { liked: on, likes: Math.max(0, p.likes + (on ? 1 : -1)) });
      try {
        const r = await likePhoto(id, p.pid, on, pin);
        patch(p.pid, { liked: r.liked, likes: r.likes });
      } catch (e) {
        patch(p.pid, { liked: p.liked, likes: p.likes });
        toast.warning(e.message);
      }
    },
    [id, pin, patch]
  );

  if (fatal)
    return (
      <Shell className="max-w-md pt-16 text-center">
        <h1 className="font-serif text-4xl font-light">{fatal.status === 404 ? "Album tidak ditemukan" : "Tidak bisa dibuka"}</h1>
        {fatal.status !== 404 && <p className="mt-4 font-sans text-sm text-[#8A8A8A]">{fatal.message}</p>}
      </Shell>
    );

  if (!pin || !data)
    return (
      <Shell className="max-w-sm pt-16">
        <Toaster position="top-center" />
        <h1 className="font-serif text-4xl font-light tracking-tight">Buka albummu</h1>
        <p className="mt-3 font-sans text-sm text-[#8A8A8A]">Masukkan PIN dari admin.</p>
        <div className="mt-8 space-y-6">
          <Field label="PIN" inputMode="numeric" maxLength={6} value={input} onChange={(e) => setInput(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && login()} />
          <Btn onClick={login} disabled={checking || input.length < 6}>{checking ? "Memeriksa…" : "Buka Album"}</Btn>
        </div>
      </Shell>
    );

  const { album, photos: all } = data;
  const top = topFavorites([...all]);
  const photos = tab === "top" ? top : all;
  const toggleSel = (pid) => setSel((s) => (s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]));
  const chosen = all.filter((p) => sel.includes(p.pid));
  const allSelected = photos.length > 0 && photos.every((p) => sel.includes(p.pid));

  // Unduh foto asli sebagai ZIP. Isi tidak dikompres dan tidak diubah, jadi resolusi utuh.
  const download = async (list, label) => {
    if (zip) return;
    if (!list.length) return toast.error("Pilih foto dulu.");
    setZip({ done: 0, total: list.length });
    const finished = [];
    let builder = new ZipBuilder();
    let size = 0;
    let lowRes = 0;
    let failed = 0;
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      try {
        const r = await fetchOriginal(id, pin, p.pid);
        if (!r.isOriginal) lowRes++;
        if (builder.count && size + r.blob.size > PART_LIMIT) {
          finished.push(builder.finish());
          builder = new ZipBuilder();
          size = 0;
        }
        await builder.add(`${String(i + 1).padStart(3, "0")}-${safe(p.by)}.${extOf(r.blob.type)}`, r.blob);
        size += r.blob.size;
      } catch {
        failed++;
      }
      setZip({ done: i + 1, total: list.length });
    }
    if (builder.count) finished.push(builder.finish());
    const base = `dirga-${slug(album.couple || album.eventName)}-${label}`;
    for (let i = 0; i < finished.length; i++) {
      save(finished[i], finished.length === 1 ? `${base}.zip` : `${base}-bagian${i + 1}.zip`);
      if (i < finished.length - 1) await sleep(900); // jeda agar browser tidak memblokir unduhan beruntun
    }
    setZip(null);
    if (!finished.length) return toast.error("Foto gagal diunduh. Coba lagi.");
    const n = list.length - failed;
    toast.success(finished.length > 1 ? `${n} foto dalam ${finished.length} file ZIP. Izinkan unduhan berganda jika diminta.` : `${n} foto masuk ke 1 file ZIP.`);
    if (lowRes) toast.message(`${lowRes} foto lama hanya tersedia dalam ukuran tampilan.`);
    if (failed) toast.warning(`${failed} foto gagal diambil.`);
  };

  const open = (list, index) => setView({ pids: list.map((x) => x.pid), index, dir: 0 });
  const live = view ? view.pids.map((pid) => all.find((q) => q.pid === pid)).filter(Boolean) : [];

  return (
    <Shell className="max-w-5xl pt-4">
      <Toaster position="top-center" />
      <h1 className="font-serif text-4xl font-light tracking-tight sm:text-5xl">{album.couple || album.eventName}</h1>
      <p className="mt-2 font-sans text-sm text-[#8A8A8A]">{album.eventName} · {dateRange(album)} · {all.length} foto</p>

      <div className="mt-8 flex flex-wrap items-center gap-3 border-y border-[#E8E3D9] py-4">
        {[["all", "Semua"], ["top", `Favorit (${top.length}/${TOP_FAVORITES})`]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={cn("px-4 py-2 font-sans text-xs uppercase tracking-widest", tab === k ? "bg-[#1C1C1C] text-white" : "border border-[#E8E3D9] bg-white")}>{l}</button>
        ))}
        <span className="flex-1" />
        <button type="button" className="font-sans text-xs uppercase tracking-widest underline" onClick={() => setSel(allSelected ? [] : photos.map((p) => p.pid))}>{allSelected ? "Batal pilih" : "Pilih semua"}</button>
        <Btn className="px-5 py-3" disabled={!!zip} onClick={() => download(chosen, "terpilih")}>
          {zip ? `Menyiapkan ${zip.done}/${zip.total}…` : `Unduh ZIP terpilih (${sel.length})`}
        </Btn>
        <Btn variant="outline" className="px-5 py-3" disabled={!!zip || top.length === 0} onClick={() => download(top, "favorit")}>Unduh ZIP favorit</Btn>
      </div>
      {zip && <p className="mt-3 font-sans text-xs text-[#8A8A8A]">Jangan tutup halaman ini sampai unduhan selesai. Foto diambil dalam ukuran asli, jadi butuh waktu.</p>}

      {photos.length === 0 ? (
        <p className="mt-16 text-center font-sans text-sm text-[#8A8A8A]">{tab === "top" ? "Belum ada foto favorit. Ketuk love di foto yang kamu suka." : "Belum ada foto dari tamu."}</p>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {photos.map((p, i) => {
            const on = sel.includes(p.pid);
            return (
              <div key={p.pid} className="relative">
                <button type="button" onClick={() => open(photos, i)} aria-label="Perbesar foto" className="block w-full">
                  <img src={p.url} alt="" loading="lazy" className={cn("aspect-square w-full object-cover transition-opacity", on && "opacity-60")} />
                </button>
                <button type="button" onClick={() => toggleSel(p.pid)} aria-pressed={on} aria-label="Pilih foto" className={cn("absolute left-2 top-2 flex h-7 w-7 items-center justify-center border", on ? "border-[#1C1C1C] bg-[#1C1C1C] text-white" : "border-white bg-white/60 text-transparent")}>
                  <Check className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => like(p)} aria-pressed={p.liked} aria-label={p.liked ? "Batal suka" : "Suka"} className="absolute right-2 top-2 flex h-7 items-center gap-1 bg-white/85 px-2 font-sans text-[11px]">
                  <Heart className="h-4 w-4" style={p.liked ? { fill: GOLD, color: GOLD } : { color: "#8A8A8A" }} />
                  {p.likes > 0 && <span>{p.likes}</span>}
                </button>
                {p.by && <span className="pointer-events-none absolute bottom-1.5 left-1.5 max-w-[80%] truncate bg-black/55 px-1.5 py-0.5 font-sans text-[10px] text-white">{p.by}</span>}
              </div>
            );
          })}
        </div>
      )}

      {view && live.length > 0 && (
        <Lightbox
          photos={live}
          index={Math.min(view.index, live.length - 1)}
          direction={view.dir}
          onIndex={(index, dir) => setView((v) => ({ ...v, index, dir }))}
          onClose={() => setView(null)}
          onLike={like}
        />
      )}
    </Shell>
  );
}
